import { LAST_5_YEARS } from '@modules/common/constants';
import {
  useCopyToClipboard,
  useIsSearchToFeedEnabled,
} from '@modules/common/hooks';
import { generateWhereClauseFromMetadataKeyValue } from '@modules/common/utils';
import { getUseInSearchLink } from '@modules/feed/utils/getUseInSearchLink';
import {
  formatFeedbackMetadataValueIfRequired,
  trackFeedbackMetadataEvent,
} from '@modules/feedback/utils';
import { OpenInNewRounded } from '@mui/icons-material';
import {
  Avatar,
  Box,
  Chip,
  Link,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { MetadataValue } from 'generated/graphql';
import { Link as RouterLink } from 'react-router-dom';

interface FeedbackMetadataValuesProps {
  displayName: string;
  values: string[];
  metadataValue?: MetadataValue;
  metadataFieldKey?: string;
  shouldAddOpenInSearchLink?: boolean;
  feedbackID: string;
}

export const FeedbackMetadataValues = ({
  displayName,
  values,
  metadataValue,
  metadataFieldKey,
  shouldAddOpenInSearchLink,
  feedbackID,
}: FeedbackMetadataValuesProps) => {
  const isSearchToFeedEnabled = useIsSearchToFeedEnabled();

  const copyTextToClipboard = useCopyToClipboard();

  if (!displayName) {
    return null;
  }

  const getTooltipTitle = (
    value: string,
    secondaryText = '(Click to copy)',
  ) => {
    return (
      <Stack spacing={0.5}>
        <Box>{value}</Box>
        <Box>{secondaryText}</Box>
      </Stack>
    );
  };

  const handleCopyTextToClipboard = (value: string) => {
    copyTextToClipboard(value);
    trackFeedbackMetadataEvent({
      eventName: 'Feedback Metadata - Copied To Clipboard',
      feedbackID,
      metadata: {
        key: displayName,
        value,
      },
    });
  };

  if (values.length > 1) {
    return (
      <Stack
        direction="row"
        flexWrap="wrap"
        alignItems="center"
        rowGap={1}
        columnGap={1}
      >
        {values.filter(Boolean).map((value) => {
          return (
            <Tooltip
              key={value}
              arrow
              placement="top-start"
              title={getTooltipTitle(value)}
            >
              <Chip
                avatar={<Avatar>{value.slice(0, 1)}</Avatar>}
                onClick={() => handleCopyTextToClipboard(value)}
                label={value}
                size="small"
              />
            </Tooltip>
          );
        })}
      </Stack>
    );
  }

  const value = formatFeedbackMetadataValueIfRequired({
    displayName,
    value: values[0],
  });

  if (shouldAddOpenInSearchLink && metadataValue && metadataFieldKey) {
    const whereClause = generateWhereClauseFromMetadataKeyValue({
      metadataFieldKey,
      metadataValue,
    });

    const searchRoute = getUseInSearchLink({
      duration: LAST_5_YEARS,
      where: whereClause,
      isSearchToFeedEnabled,
    });

    return (
      <Tooltip
        arrow
        placement="top-start"
        title={getTooltipTitle(
          value,
          `(Click to open in ${isSearchToFeedEnabled ? 'Feed' : 'Search'})`,
        )}
      >
        <Link
          component={RouterLink}
          to={searchRoute}
          target="_blank"
          rel="noreferrer"
          underline="hover"
          onClick={() => {
            trackFeedbackMetadataEvent({
              eventName: 'Feedback Metadata - Opened In Search Page',
              feedbackID,
              metadata: {
                key: displayName,
                value,
              },
            });
          }}
        >
          <Stack
            component={Stack}
            direction="row"
            alignItems="center"
            spacing={0.5}
          >
            <Typography variant="body2" noWrap>
              {value}
            </Typography>
            <OpenInNewRounded
              sx={{
                fontSize: 'inherit',
              }}
            />
          </Stack>
        </Link>
      </Tooltip>
    );
  }

  return (
    <Tooltip arrow placement="top-start" title={getTooltipTitle(value)}>
      <Typography
        variant="body2"
        sx={{
          cursor: 'pointer',
        }}
        onClick={() => handleCopyTextToClipboard(value)}
        noWrap
      >
        {value}
      </Typography>
    </Tooltip>
  );
};
